import type { PriceStrategy } from "@/lib/channels";
import {
  buildPricingProviderConfig,
  intendedProviderPrice,
  priceFormConfig,
  pricingConfigFromProviderConfig,
  type PriceStrategyConfig,
} from "@/lib/listing-pricing";
import { isValidSellingPrice, sellingPriceError } from "@/lib/listing-pricing";
import { effectiveSellingPrice } from "@/lib/products";

/**
 * Channel price for product create.
 * Precedence: variant or product base, then the connector default rule,
 * then an explicit product override. The override replaces the connector
 * rule. It does not stack on top of it. Variant base uses the same
 * catalogue fallback as the edge effectiveVariantPrice helper, then
 * intendedProviderPrice, the helper listing sync uses.
 */

const STRATEGIES: PriceStrategy[] = ["inherit", "fixed_override", "fixed_markup", "percentage_markup"];

export interface ConnectorPriceRule {
  strategy: PriceStrategy;
  config: PriceStrategyConfig;
}

export interface WizardPriceOverride {
  strategy: PriceStrategy;
  amount: string;
}

export interface WizardVariantBasePrice {
  sku: string;
  sellingPrice: number | null;
}

export interface WizardChannelPriceLine {
  sku: string;
  base: number;
  intended: number;
}

export interface WizardChannelPriceQuote {
  source: "base" | "connector_default" | "product_override";
  strategy: PriceStrategy;
  config: PriceStrategyConfig;
  lines: WizardChannelPriceLine[];
  min: number;
  max: number;
}

export function isPriceStrategy(value: unknown): value is PriceStrategy {
  return typeof value === "string" && STRATEGIES.includes(value as PriceStrategy);
}

export function connectorPriceRuleFromConfig(config: unknown): ConnectorPriceRule {
  const root = config && typeof config === "object" ? config as Record<string, unknown> : {};
  const pricing = root.pricing && typeof root.pricing === "object" ? root.pricing as Record<string, unknown> : {};
  const strategy = isPriceStrategy(pricing.strategy) ? pricing.strategy : "inherit";
  return {
    strategy,
    config: pricingConfigFromProviderConfig({ pricing }),
  };
}

export function mergeConnectorPricing(
  config: Record<string, unknown> | null | undefined,
  strategy: PriceStrategy,
  amount: number | null,
): Record<string, unknown> {
  return {
    ...(config ?? {}),
    ...connectorPricingPatch(strategy, amount),
  };
}

/**
 * Approved-key patch for `update_connector_merchant_config`. Only `pricing` is
 * merchant-editable, so the patch never carries provider-owned connector state.
 * The server merges this into connectors.config, preserving unrelated keys.
 */
export function connectorPricingPatch(
  strategy: PriceStrategy,
  amount: number | null,
): Record<string, unknown> {
  const built = buildPricingProviderConfig(strategy, amount);
  const pricing = built.pricing as Record<string, unknown>;
  return { pricing: { ...pricing, strategy } };
}

function ruleAmount(strategy: PriceStrategy, config: PriceStrategyConfig): number | null {
  if (strategy === "percentage_markup") return config.percentageMarkup ?? null;
  if (strategy === "fixed_markup") return config.fixedMarkup ?? null;
  if (strategy === "fixed_override") return config.fixedOverride ?? null;
  return null;
}

export function resolveWizardChannelPrice(input: {
  productPrice: number | null;
  variants: WizardVariantBasePrice[];
  connectorDefault: ConnectorPriceRule;
  productOverride: WizardPriceOverride | null;
}): WizardChannelPriceQuote {
  const overrideActive = input.productOverride != null;
  const strategy = overrideActive ? input.productOverride!.strategy : input.connectorDefault.strategy;
  const config = overrideActive
    ? priceFormConfig(input.productOverride!.strategy, input.productOverride!.amount)
    : input.connectorDefault.config;
  const bases = input.variants.length > 0
    ? input.variants
    : [{ sku: "", sellingPrice: null }];
  const lines = bases.map((variant) => {
    const base = effectiveSellingPrice(variant.sellingPrice, input.productPrice) ?? 0;
    return {
      sku: variant.sku,
      base,
      intended: intendedProviderPrice(strategy, base, config, null),
    };
  });
  const amounts = lines.map((line) => line.intended);
  const source = overrideActive
    ? "product_override"
    : strategy === "inherit"
      ? "base"
      : "connector_default";
  return {
    source,
    strategy,
    config,
    lines,
    min: Math.min(...amounts),
    max: Math.max(...amounts),
  };
}

/** Active product-override amounts. Blank never becomes zero. Negative markups stay valid. */
export function wizardPriceAmountError(strategy: PriceStrategy, amountRaw: string): string | null {
  if (strategy === "inherit") return null;
  const raw = amountRaw.trim();
  if (raw === "") return "Enter an amount for this price rule.";
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return "Enter a valid number.";
  if (strategy === "fixed_override" && amount <= 0) return "Fixed price must be greater than zero.";
  return null;
}

/** Resulting channel prices, not just the rule amount. Negative markups stay valid when every line stays above zero. */
export function wizardQuotePriceError(quote: WizardChannelPriceQuote): string | null {
  for (const line of quote.lines) {
    const error = sellingPriceError(line.intended);
    if (error) {
      return line.sku
        ? `${line.sku}: ${error}`
        : error;
    }
  }
  return null;
}

export function connectorDefaultAmount(
  strategy: PriceStrategy,
  amountRaw: string,
): { ok: true; amount: number | null } | { ok: false; error: string } {
  const error = wizardPriceAmountError(strategy, amountRaw);
  if (error) return { ok: false, error };
  if (strategy === "inherit") return { ok: true, amount: null };
  return { ok: true, amount: Number(amountRaw) };
}

export function wizardListingPricePayload(quote: WizardChannelPriceQuote): {
  priceStrategy: PriceStrategy;
  providerConfig: Record<string, unknown>;
} | null {
  const amount = ruleAmount(quote.strategy, quote.config);
  if (quote.strategy !== "inherit") {
    if (amount == null || !Number.isFinite(amount)) return null;
    if (quote.strategy === "fixed_override" && amount <= 0) return null;
  }
  if (quote.lines.some((line) => !isValidSellingPrice(line.intended))) return null;
  return {
    priceStrategy: quote.strategy,
    providerConfig: buildPricingProviderConfig(quote.strategy, amount),
  };
}

export function formatWizardPriceRange(min: number, max: number): string {
  const money = (n: number) => `A$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (min === max) return money(min);
  return `${money(min)} – ${money(max)}`;
}
